import { useEffect, useState } from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import TextFieldDate from "../UI/TextFieldDate";

const SWITCH_PROPS = { size: "small" };

/** Diálogo de cadastro/edição de uma disciplina na turma. */
const DisciplinaTurmaForm = ({ tdp, onClose, onSave, isSaving, unregisteredDisciplinas, professores, livros }) => {
  const [form, setForm] = useState(null);

  useEffect(() => {
    setForm(tdp ? { ...tdp } : null);
  }, [tdp]);

  const eNovo = !form?.codigo;
  const set = (campo, valor) => setForm((prevState) => ({ ...prevState, [campo]: valor }));

  const handleSwitch = (campo) => (event) => {
    const checked = event.target.checked;
    setForm((prevState) => ({
      ...prevState,
      [campo]: checked,
      // Eletiva e Projeto são exclusivos.
      ...(campo === "eEletiva" && checked ? { eProjeto: false } : {}),
      ...(campo === "eProjeto" && checked ? { eEletiva: false } : {}),
    }));
  };

  const somenteNumeros = (campo, max) => (event) => set(campo, event.target.value.replace(/\D/g, "").slice(0, max));

  const podeSalvar = Boolean(form?.disciplina) && !isSaving;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!podeSalvar) return;
    onSave(form);
  };

  return (
    <Dialog open={Boolean(tdp)} onClose={onClose} fullWidth maxWidth="sm">
      {form && (
        <form onSubmit={handleSubmit}>
          <DialogTitle>{eNovo ? "Adicionar Disciplina" : form.disciplina?.descricao}</DialogTitle>
          <DialogContent>
            <Stack spacing={2.5} pt={1}>
              {eNovo && (
                <TextField
                  select
                  size="small"
                  fullWidth
                  required
                  label="Disciplina"
                  value={form.disciplina?.codDisciplina ?? ""}
                  onChange={(event) =>
                    set(
                      "disciplina",
                      unregisteredDisciplinas.find((d) => d.codDisciplina === event.target.value),
                    )
                  }
                >
                  {unregisteredDisciplinas.map((d) => (
                    <MenuItem key={d.codDisciplina} value={d.codDisciplina}>
                      {d.descricao}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Professor"
                    value={professores.some((p) => p.usuario.id === form.usuario?.id) ? form.usuario.id : ""}
                    onChange={(event) =>
                      set("usuario", professores.find((p) => p.usuario.id === event.target.value)?.usuario ?? null)
                    }
                  >
                    <MenuItem value="">
                      <em>Nenhum</em>
                    </MenuItem>
                    {professores.map((p) => (
                      <MenuItem key={p.usuario.id} value={p.usuario.id}>
                        {p.usuario.nome}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Livro"
                    value={livros.some((l) => l.codLivro === form.livro?.codLivro) ? form.livro.codLivro : ""}
                    onChange={(event) =>
                      set("livro", livros.find((l) => l.codLivro === event.target.value) ?? null)
                    }
                  >
                    <MenuItem value="">
                      <em>Nenhum</em>
                    </MenuItem>
                    {livros.map((l) => (
                      <MenuItem key={l.codLivro} value={l.codLivro}>
                        {l.descricao}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Carga horária"
                    value={form.cargaHoraria ?? ""}
                    onChange={(event) => set("cargaHoraria", event.target.value)}
                    slotProps={{ htmlInput: { maxLength: 10 } }}
                  />
                </Grid>
              </Grid>

              <Divider />

              <Box>
                <Typography variant="subtitle2" mb={0.5}>
                  Tipo e opções
                </Typography>
                <Stack direction="row" flexWrap="wrap" columnGap={2}>
                  <FormControlLabel
                    control={<Switch {...SWITCH_PROPS} checked={Boolean(form.eEletiva)} onChange={handleSwitch("eEletiva")} />}
                    label="Eletiva"
                  />
                  <FormControlLabel
                    control={<Switch {...SWITCH_PROPS} checked={Boolean(form.eProjeto)} onChange={handleSwitch("eProjeto")} />}
                    label="Projeto"
                  />
                  <FormControlLabel
                    control={
                      <Switch {...SWITCH_PROPS} checked={Boolean(form.escolhaAluno)} onChange={handleSwitch("escolhaAluno")} />
                    }
                    label="Escolha do aluno"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        {...SWITCH_PROPS}
                        checked={Boolean(form.enviarNotificacaoPresenca)}
                        onChange={handleSwitch("enviarNotificacaoPresenca")}
                      />
                    }
                    label="Enviar notificação de presença"
                  />
                </Stack>
              </Box>

              {form.escolhaAluno && (
                <TextField
                  size="small"
                  label="Capacidade máxima"
                  value={form.capacidadeMaxima ?? ""}
                  onChange={somenteNumeros("capacidadeMaxima", 3)}
                  slotProps={{ htmlInput: { inputMode: "numeric" } }}
                  sx={{ maxWidth: 240 }}
                />
              )}

              {form.eProjeto && (
                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch {...SWITCH_PROPS} checked={Boolean(form.eDataSerie)} onChange={handleSwitch("eDataSerie")} />
                    }
                    label="Usar as datas da série"
                  />
                  {!form.eDataSerie && (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextFieldDate
                          label="Data início"
                          value={form.dataInicio ?? null}
                          onChange={(data) => set("dataInicio", dayjs(data))}
                          slotProps={{ textField: { size: "small", fullWidth: true } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextFieldDate
                          label="Data fim"
                          value={form.dataFim ?? null}
                          onChange={(data) => set("dataFim", dayjs(data))}
                          slotProps={{ textField: { size: "small", fullWidth: true } }}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={!podeSalvar}>
              {eNovo ? "Adicionar" : "Salvar"}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default DisciplinaTurmaForm;
